import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useScrollReveal, fadeUpVariants } from '../hooks/useScrollReveal'
import { useAdmin } from '../context/AdminContext'
import { FaCrown, FaJava } from 'react-icons/fa'
import {
  SiReact, SiMongodb, SiHtml5, SiJavascript, SiNodedotjs, SiExpress,
  SiTailwindcss, SiFigma, SiCanva, SiPython, SiGithub, SiGit, SiDocker,
  SiFirebase, SiMysql, SiPostgresql, SiTypescript, SiCss
} from 'react-icons/si'

const ICON_MAP = {
  'React': <SiReact />,
  'React.js': <SiReact />,
  'React JS': <SiReact />,
  'MongoDB': <SiMongodb />,
  'HTML5': <SiHtml5 />,
  'HTML': <SiHtml5 />,
  'CSS3': <SiCss />,
  'CSS': <SiCss />,
  'JavaScript': <SiJavascript />,
  'JS': <SiJavascript />,
  'Node.js': <SiNodedotjs />,
  'Node': <SiNodedotjs />,
  'Node JS': <SiNodedotjs />,
  'Express': <SiExpress />,
  'Express.js': <SiExpress />,
  'Tailwind CSS': <SiTailwindcss />,
  'Tailwind': <SiTailwindcss />,
  'Figma': <SiFigma />,
  'Canva': <SiCanva />,
  'Python': <SiPython />,
  'Java': <FaJava />,
  'GitHub': <SiGithub />,
  'Git': <SiGit />,
  'Docker': <SiDocker />,
  'Firebase': <SiFirebase />,
  'MySQL': <SiMysql />,
  'PostgreSQL': <SiPostgresql />,
  'TypeScript': <SiTypescript />,
  'TS': <SiTypescript />
}


export default function Skills() {
  const { ref, controls } = useScrollReveal()
  const { siteData } = useAdmin()
  const tools = siteData?.tools || []

  if (tools.length === 0) return null

  return (
    <section id="skills" className="py-24 relative overflow-hidden flex flex-col items-center">
      <Helmet>
        <title>Skills & Technologies | Shiv Shankar Pandit</title>
        <meta name="description" content="Tools, languages, and technologies I specialize in." />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full flex flex-col items-center">
        
        {/* Header */}
        <motion.div variants={fadeUpVariants} initial="hidden" animate="visible" className="text-center mb-20 md:mb-28">
          <h1 className="section-heading mb-3">
            My <span className="gradient-text">Skills</span>
          </h1>
          <p className="text-muted text-sm md:text-base max-w-md mx-auto">
            Creative expertise meets technical excellence
          </p>
        </motion.div>

        {/* Orbiting Layout */}
        <div className="relative w-[220px] h-[220px] sm:w-[280px] sm:h-[280px] md:w-[450px] md:h-[450px] flex items-center justify-center mt-8">
          
          {/* Central Glow & Circle */}
          <div className="absolute inset-0 m-auto w-[120px] h-[120px] md:w-[180px] md:h-[180px] rounded-full bg-purple-500/20 blur-2xl animate-pulse" />
          
          <div className="relative z-10 w-[110px] h-[110px] md:w-[160px] md:h-[160px] bg-white rounded-full flex flex-col items-center justify-center shadow-xl border-4 border-white">
            <FaCrown className="text-purple-500 text-3xl md:text-5xl mb-1 md:mb-2" />
            <span className="text-slate-800 font-bold text-lg md:text-xl">Skills</span>
            <span className="text-slate-400 text-[9px] md:text-xs">expertise & passion</span>
          </div>

          {/* Orbit Container */}
          <motion.div
            initial={{ rotate: 0 }}
            animate={{ rotate: 360 }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            className="absolute w-full h-full rounded-full flex items-center justify-center pointer-events-none"
          >
            {tools.map((tool, i) => {
              const angle = (i / tools.length) * 360
              return (
                <div
                  key={tool.id}
                  className="absolute pointer-events-auto"
                  style={{
                    transform: `rotate(${angle}deg) translateX(50%)`,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    left: 0,
                    top: 0
                  }}
                >
                  <motion.div
                    className="absolute w-[60px] h-[60px] md:w-[85px] md:h-[85px] rounded-full bg-white shadow-lg border-[3px] border-purple-100 flex flex-col items-center justify-center cursor-pointer"
                    initial={{ rotate: -angle }}
                    animate={{ rotate: -(angle + 360) }}
                    transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                    whileHover={{ scale: 1.15, zIndex: 20 }}
                  >
                    <span className="text-xl md:text-2xl leading-none" style={{ color: tool.color || '#a855f7' }}>{ICON_MAP[tool.name] || tool.icon}</span>
                    <span className="text-slate-800 font-bold text-[9px] md:text-[11px] mt-0.5 md:mt-1">{tool.name}</span>
                    <span className="text-purple-400 font-mono text-[7px] md:text-[9px] leading-tight">{tool.percentage || 80}%</span>
                  </motion.div>
                </div>
              )
            })}
          </motion.div>
        </div>

      </div>
    </section>
  )
}
